/**
 * The shared column rule bound to ONE draft exercise: which columns its table shows, and which it
 * actually prescribes.
 *
 * The rule itself is `planBuilderColumns` in `~/shared/measurement` and is not restated here. What
 * this module owns is the PROJECTION the rule needs from the draft — an exercise's prescriptions
 * are its base sets **plus** its per-cycle manual overrides, because a load authored only in a
 * week-4 deload still has to open its column.
 *
 * ── And the inverse act: taking a column AWAY ────────────────────────────────────────────────
 * `removeColumnValues` is the mobile original's other half, ported once the ⋯ grew the affordance
 * that calls it. Clearing is not a side effect of hiding; it IS the hiding — values are the column
 * state, so a column with nothing behind it is a column the trainer is not prescribing, and that
 * reading survives the save and every session after it with nothing stored to say so.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { planBuilderColumns, type MeasurementFieldKey, type MeasurementValues } from '~/shared/measurement';

import type { ExerciseDraft, ProgressionOverride, SetDraft } from './planDraft.types';
import { isLadderPercentOverride } from './progressionMatrix';
import { measurementValueKey, type SetValueKey } from './setGridFields';
import { derivesLoadInSession, isAmrapSet } from './setShape';

/** The exercise fields a column question reads — nothing else of the draft is involved. */
type ColumnSource = Pick<ExerciseDraft, 'measurementFields' | 'sets' | 'progressionOverrides'>;

/**
 * Every prescription the exercise carries across EVERY cycle: its authored base sets plus each
 * per-cycle override.
 *
 * The load lands under `weight` from whichever of the two cells carries it. The weight column is
 * the one column that is not one-to-one with a field — it writes kilograms in absolute mode and a
 * fraction of 1RM in percent mode — so reading only `prescribedWeight` would hide the column on
 * every percent-prescribed exercise.
 */
function exercisePrescriptions(exercise: ColumnSource): MeasurementValues[] {
  const fromSets = exercise.sets.map(
    (set): MeasurementValues => ({
      weight: set.prescribedWeight ?? set.prescribedPercent ?? undefined,
      reps: set.prescribedReps ?? undefined,
      duration: set.prescribedDurationSeconds ?? undefined,
      distance: set.prescribedDistanceMeters ?? undefined,
      rounds: set.prescribedRounds ?? undefined,
      rpe: set.prescribedRpe ?? undefined,
      rir: set.prescribedRir ?? undefined,
    }),
  );
  const fromOverrides = exercise.progressionOverrides.map(
    (override): MeasurementValues => ({
      weight: override.prescribedWeight ?? override.prescribedPercent ?? undefined,
      rpe: override.prescribedRpe ?? undefined,
      rir: override.prescribedRir ?? undefined,
    }),
  );
  return [...fromSets, ...fromOverrides];
}

/**
 * The measurement columns the TABLE shows: what the exercise prescribes, plus whatever the trainer
 * opened this editing session, all inside what the exercise measures.
 */
export function exerciseColumns(exercise: ExerciseDraft): MeasurementFieldKey[] {
  return planBuilderColumns({
    measured: exercise.measurementFields,
    prescribed: exercisePrescriptions(exercise),
    revealed: exercise.revealedColumns,
  });
}

/**
 * The columns the exercise actually PRESCRIBES — the same rule with the editing-session reveal left
 * out.
 *
 * A second entry point rather than a flag, because the difference matters exactly once and matters
 * a lot: a SUMMARY may only speak about what is prescribed. A column the trainer merely opened is
 * empty by definition, so reading the table's answer would make every freshly inserted strength
 * exercise announce "dobiera klient" before its trainer had typed anything — a claim about a
 * decision nobody has made yet.
 */
export function prescribedColumns(exercise: ColumnSource): MeasurementFieldKey[] {
  return planBuilderColumns({
    measured: exercise.measurementFields,
    prescribed: exercisePrescriptions(exercise),
  });
}

/**
 * Take a column away by clearing what is behind it — across every set AND every per-cycle override.
 *
 * The OVERRIDES have to go with the sets or the promise is false: the column rule reads both, so a
 * load pinned on cycle 4 would hold the column open — empty, unremovable, and back again after a
 * reload.
 *
 * WEIGHT clears both of its cells, kilograms and %-of-1RM, or flipping the prescription mode would
 * reopen the column with the other one still in it.
 *
 * ── The one thing it does not touch is SET CONSTRUCTION ───────────────────────────────────────
 * A measurement value is never required, but the values describing how a set is BUILT are hard at
 * save: an AMRAP set needs its rep floor, and a ladder member needs its percent of the top. Those
 * live in the same two cells the reps and weight columns write, so clearing unconditionally
 * produces a plan the backend refuses — a generic save failure over a field the trainer no longer
 * has a column for, with no way out from inside the app.
 *
 * Skipping is chosen over the other available answer, RESOLVING the shape — retyping the AMRAP set
 * and dissolving the ladder. Removing a column is a statement about what is prescribed, not about
 * what the sets ARE, and silently converting a back-off block into ordinary working sets from a
 * menu entry discards authored work nobody offered up. The visible cost is that such a column does
 * not disappear: it keeps rendering the descent's percent or the AMRAP's floor, because there
 * genuinely is still a prescription there and nothing but the set-type menu can remove it.
 *
 * Ported from mobile's `removeColumnValues`, carve-outs included, because the two clients edit the
 * same plan and a column removed on one has to mean the same thing on the other.
 */
export function removeColumnValues(
  exercise: ExerciseDraft,
  field: MeasurementFieldKey,
): ExerciseDraft {
  const valueKey = measurementValueKey(field);
  // Speed and tempo have no cell on the set, so there is nothing behind their column to clear.
  if (valueKey === null) return exercise;
  return {
    ...exercise,
    sets: exercise.sets.map((set) => ({ ...set, ...clearedCells(set, field, valueKey) })),
    progressionOverrides: survivingOverrides(exercise, field),
  };
}

/** What clearing `field` writes to ONE set, with that set's own construction left standing. */
function clearedCells(
  set: SetDraft,
  field: MeasurementFieldKey,
  valueKey: SetValueKey,
): Partial<SetDraft> {
  if (field === 'weight') {
    // A ladder member's percent is not a weight prescription — it is the descent's whole
    // prescription, and the backend requires one on every member. Its `prescribedWeight` is
    // already null by construction.
    return derivesLoadInSession(set)
      ? { prescribedWeight: null }
      : { prescribedWeight: null, prescribedPercent: null };
  }
  // An AMRAP set's reps are its rep FLOOR — the minimum that counts as delivering it, required at
  // save and read by the engine.
  if (field === 'reps' && isAmrapSet(set)) return {};
  return { [valueKey]: null };
}

/** The `ProgressionOverride` fields a column's removal invalidates. A column with none is never pinned. */
const OVERRIDE_FIELDS: Partial<Record<MeasurementFieldKey, readonly (keyof ProgressionOverride)[]>> = {
  weight: ['prescribedWeight', 'prescribedPercent'],
  rpe: ['prescribedRpe'],
  rir: ['prescribedRir'],
};

/**
 * The per-cycle pins that survive removing `field`. An override carries its value on exactly one of
 * its four value fields, so one whose field is being cleared has nothing left to say and is dropped
 * whole.
 *
 * A LADDER PERCENT is kept for the same reason the sets keep theirs: it is the descent's per-cycle
 * prescription, the only one the backend accepts on such a row, and it sits on `prescribedPercent`
 * only because that field is overloaded — the row it is pinned to is what tells the two apart.
 */
function survivingOverrides(
  exercise: Pick<ExerciseDraft, 'sets' | 'progressionOverrides'>,
  field: MeasurementFieldKey,
): ProgressionOverride[] {
  const pinned = OVERRIDE_FIELDS[field];
  if (pinned === undefined) return [...exercise.progressionOverrides];
  return exercise.progressionOverrides.filter(
    (override) =>
      isLadderPercentOverride(override, exercise.sets) ||
      !pinned.some((key) => override[key] != null),
  );
}
